import React, { useEffect, useState } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AUTH_PATHS,
  OAUTH_AUTHORIZE_LOGIN_PATH,
  ONBOARDING_PATHS,
  TELEGRAM_AUTH_PATH,
  UNIVERSAL_LINKS_PATHS,
  WHOP_LANDING_PATH,
} from "common/constants";
import useDarkMode from "utils/hooks/useDarkMode";
import useIntercom from "utils/hooks/useIntercom";
import PrivateRoute from "common/Routes/PrivateRoute";
import { useSaveSearchParams } from "utils/hooks/useSearchParams";
import usePageTracking from "utils/hooks/usePageTracking";
import RedirectWithQuery from "common/Routes/RedirectWithQuery";
import { selectWorkspace } from "@shared/slices/common.slice";
import { openMessage } from "utils/misc";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useDisplayLinkMessages, useScrollToInputs } from "utils/hooks/misc";
import { useRestrictedCountries } from "utils/hooks/useRestrictedCountries";
import AppDownloadRedirect from "features/settings/AppDownloadRedirect";
import { LOGIN_UNIQUE_ID_STORAGE_KEY } from "@shared/common/constants";
import { v4 as uuidv4 } from "uuid";
import { sharedLib } from "@shared/services/shared.service";
import { AuthProvider, useAuthContext } from "common/Auth/AuthContext";
import useInitializeWorkspace from "utils/hooks/useInitializeWorkspace";
import AnalyticsProvider from "analytics/AnalyticsProvider";
import WhopProvider from "features/whop/WhopProvider.tsx";
import * as Sentry from "@sentry/react";
import QueriesError from "common/Empty/QueriesError.tsx";
import ExternalConnectAccount from "features/configuration/InviteExternalUsers/ExternalConnectAccount.tsx";
import { useWebViewBridge } from "services/webview/useWebViewBridge.ts";
import useHistoryTracker from "utils/hooks/useHistoryTracker.ts";
import { usePreventScroll } from "utils/hooks/usePreventScroll.ts";
import TelegramAuthPage from "features/auth/TelegramAuthPage";
import Home from "common/Home/Home.tsx";
import Auth from "features/auth/Auth.tsx";
import OAuthAuthorizeLoginPage from "features/auth/OAuthAuthorizeLoginPage";
import PageSizeProvider from "common/Home/PageWrapper/PageSizeProvider.tsx";
import { checkAppExternalRedirects } from "utils/routes.tsx";
import useQuery from "utils/hooks/useQuery.ts";
import { useExternalInviteToken } from "features/configuration/InviteExternalUsers/hooks.tsx";
import AppThemeProvider from "features/misc/AppThemeProvider.tsx";
import { useAppBranding } from "utils/hooks/useBranding";
import BrandingProvider from "features/misc/BrandingProvider";
import NewLookProvider from "features/newLook/NewLookProvider.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding";
import useTelegramLogin from "features/auth/useTelegramLogin.ts";
import TelegramWebAppProvider from "features/telegram/TelegramWebAppProvider.tsx";
import WhopLandingPage from "features/whop/whopLanding/WhopLandingPage";
import ProgressToasts from "common/ProgressToasts.tsx";

const App = () => {
  useWebViewBridge();
  usePreventScroll();
  useHistoryTracker();
  useScrollToInputs();

  return (
    <PageSizeProvider>
      <NewLookProvider>
        <BrandingProvider>
          <Sentry.ErrorBoundary
            fallback={<QueriesError isGeneralPageError={true} />}
          >
            <AppThemeProvider>
              <AnalyticsProvider>
                <TelegramWebAppProvider>
                  <WhopProvider>
                    <AuthProvider>
                      <InnerApp />
                    </AuthProvider>
                  </WhopProvider>
                  <ProgressToasts />
                </TelegramWebAppProvider>
              </AnalyticsProvider>
            </AppThemeProvider>
          </Sentry.ErrorBoundary>
        </BrandingProvider>
      </NewLookProvider>
    </PageSizeProvider>
  );
};

export default React.memo(App);

const InnerApp = () => {
  useDarkMode();
  useSaveSearchParams();
  usePageTracking();
  useDisplayLinkMessages();
  useRestrictedCountries();
  useInitializeWorkspace();
  useIntercom();
  useAppBranding();
  const { isTelegramLogin } = useTelegramLogin();
  const [isLoading, setIsLoading] = useState(true);
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const isLocalStorageTokenLoaded = useSelector(
    sharedLib.selectors.authentication.selectIsLoaded
  );
  const { isLoggingOut } = useAuthContext();
  const workspace = useSelector(selectWorkspace);
  const location = useLocation();
  const query = useQuery();

  const redirect = checkAppExternalRedirects(location, query);
  const externalInviteToken = useExternalInviteToken();
  const { getBrandingDataStatus } = useBranding();

  useEffect(() => {
    if (!localStorage?.getItem(LOGIN_UNIQUE_ID_STORAGE_KEY)) {
      localStorage?.setItem(LOGIN_UNIQUE_ID_STORAGE_KEY, uuidv4());
    }
  }, []);

  useEffect(() => {
    if (isLocalStorageTokenLoaded && workspace) {
      setIsLoading(false); // Storing value locally needed to persist through logouts which clear the store
    }
  }, [isLocalStorageTokenLoaded, workspace]);

  const finalIsLoading =
    isLoggingOut ||
    isLoading ||
    (getBrandingDataStatus.isLoading && !externalInviteToken) ||
    isTelegramLogin;

  const getContent = () => {
    if (finalIsLoading) return;
    if (externalInviteToken) return <ExternalConnectAccount />;

    return (
      <Switch>
        <Route exact path={WHOP_LANDING_PATH} component={WhopLandingPage} />
        <Route path={UNIVERSAL_LINKS_PATHS} component={AppDownloadRedirect} />
        <Route exact path={TELEGRAM_AUTH_PATH} component={TelegramAuthPage} />
        <Route
          exact
          path={OAUTH_AUTHORIZE_LOGIN_PATH}
          render={() => (token ? <OAuthAuthorizeLoginPage /> : <Auth />)}
        />
        <Route
          exact
          path={AUTH_PATHS}
          render={() => {
            if (ONBOARDING_PATHS.includes(location.pathname)) {
              return <Home />;
            }
            return token ? (
              <RedirectWithQuery to="/" queryKeysToDelete={["token"]} />
            ) : (
              <Auth />
            );
          }}
        />
        <Route
          exact
          path="/email-confirmed"
          render={() => {
            openMessage(
              "Your email has been confirmed successfully! You can now log in.",
              "success",
              8
            );
            return <Redirect to="/login" />;
          }}
        />
        <PrivateRoute path="/" component={Home} />
      </Switch>
    );
  };

  if (redirect) return redirect;

  return (
    <LoadingSpinnerOverlay
      isLoading={finalIsLoading}
      hideContentWhileLoading={true}
      spinnerSize="large"
    >
      {getContent()}
    </LoadingSpinnerOverlay>
  );
};
